import React from 'react'
import styled from 'styled-components'
import { StandardColumn } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'

export const fashionOrdersScreenKey = 'fashion_orders'

export type OrderType = {
  id: number
  clientId?: number | null
  client?: string | null
  seller?: string | null
  myCnpj?: string | null
  orderType?: string | null
  representative?: string | null
  orderSituation?: string | null
  generalClass?: string | null
  billingDate?: string | null
  clientOrderNumber?: number | string | null
  sellingPrice?: number | null
  extraInfo?: string | null
  criticalAnalysisDesc?: string | null
  shippingType?: string | null
  shippingValue?: number | null
  carrier?: string | null
  trackingCode?: string | null
  paymentCondition?: string | null
  paymentMethod?: string | null
  installmentCount?: number | null
  orderItemsSummary?: string | null
  createdAt: string
  startDateOfDelivery?: string | null
  orderDate?: string | null
  quantityItems?: string | number | null
  quantityPieces?: string | number | null
  status?: boolean | null
  totalCost?: number | null
  total?: number | null
  saleStatus?: string | null
  proposalNumber?: number | null
  saleNumber?: number | null
}

const SaleStatusBadge = styled.span<{ $variant: 'closed' | 'open' | 'cancelled' }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  ${props => props.$variant === 'closed' ? `
    background-color: #E9FBEF;
    color: #16A34A;
    border: 1px solid #A7E2BA;
  ` : props.$variant === 'cancelled' ? `
    background-color: #FDECEC;
    color: #DC2626;
    border: 1px solid #F3B4B4;
  ` : `
    background-color: #F8F9FA;
    color: #6B7280;
    border: 1px solid #E5E7EB;
  `}
`

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-'
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return '-'
    return d.toLocaleDateString('pt-BR')
  } catch {
    return '-'
  }
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—'
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const getFirstWithEllipsis = (content: string): string => {
  if (!content) return '-'
  const parts = content.split(',').map((s) => s.trim()).filter(Boolean)
  if (parts.length === 0) return '-'
  if (parts.length === 1) return parts[0]
  return `${parts[0]}...`
}

const buildTooltipText = (content: string): string => {
  if (!content) return ''
  const items = content.split(',').map((s) => s.trim()).filter(Boolean)
  return items.length > 1 ? items.join('\n') : content
}

const summarizedCell = (value?: string | null) => {
  const str = value || '-'
  return (
    <OverflowCell text={buildTooltipText(str)} forceTooltip={str.includes(',') || str.length > 20}>
      {getFirstWithEllipsis(str)}
    </OverflowCell>
  )
}

const getSaleStatusVariant = (saleStatus?: string | null): 'closed' | 'open' | 'cancelled' => {
  if (saleStatus === 'Fechado') return 'closed'
  if (saleStatus === 'Cancelado') return 'cancelled'
  return 'open'
}

const getTotalValue = (order: OrderType): number => Number(order.totalCost) || Number(order.total) || 0

export const buildFashionOrderColumns = (): StandardColumn<OrderType>[] => [
  {
    id: 'id',
    label: 'Nº do pedido',
    cell: (order) => (
      order.saleStatus === 'Fechado' && order.saleNumber != null
        ? `Venda nº ${order.saleNumber}`
        : order.proposalNumber != null
          ? `Proposta nº ${order.proposalNumber}`
          : String(order.id)
    ),
  },
  {
    id: 'seller',
    label: 'Vendedor',
    cell: (order) => summarizedCell(order.seller),
  },
  {
    id: 'myCnpj',
    label: 'Meu CNPJ',
    cell: (order) => summarizedCell(order.myCnpj),
  },
  {
    id: 'orderType',
    label: 'Tipo do pedido',
    cell: (order) => summarizedCell(order.orderType),
  },
  {
    id: 'representative',
    label: 'Representante',
    cell: (order) => summarizedCell(order.representative),
  },
  {
    id: 'orderSituation',
    label: 'Situação do Pedido',
    cell: (order) => summarizedCell(order.orderSituation),
  },
  {
    id: 'generalClass',
    label: 'Classe Geral',
    cell: (order) => summarizedCell(order.generalClass),
  },
  {
    id: 'billingDate',
    label: 'Data de Faturamento',
    accessor: (order) => order.billingDate || '',
    cell: (order) => formatDate(order.billingDate),
  },
  {
    id: 'clientOrderNumber',
    label: 'Nº do Pedido do Cliente',
    cell: (order) => order.clientOrderNumber ?? '-',
  },
  {
    id: 'extraInfo',
    label: 'Informação extra',
    cell: (order) => summarizedCell(order.extraInfo),
  },
  {
    id: 'criticalAnalysisDesc',
    label: 'Descrição da Análise',
    cell: (order) => summarizedCell(order.criticalAnalysisDesc),
  },
  {
    id: 'shippingType',
    label: 'Tipo de frete',
    cell: (order) => summarizedCell(order.shippingType),
  },
  {
    id: 'shippingValue',
    label: 'Valor do frete',
    accessor: (order) => Number(order.shippingValue) || 0,
    cell: (order) => formatCurrency(order.shippingValue),
    exportValue: (order) => formatCurrency(order.shippingValue),
  },
  {
    id: 'carrier',
    label: 'Transportadora',
    cell: (order) => summarizedCell(order.carrier),
  },
  {
    id: 'trackingCode',
    label: 'Código de rastreio',
    cell: (order) => summarizedCell(order.trackingCode),
  },
  {
    id: 'paymentCondition',
    label: 'Condição de pagamento',
    cell: (order) => summarizedCell(order.paymentCondition),
  },
  {
    id: 'paymentMethod',
    label: 'Forma de pagamento',
    cell: (order) => summarizedCell(order.paymentMethod),
  },
  {
    id: 'installments',
    label: 'Parcelas',
    cell: (order) => (order.installmentCount ? `${order.installmentCount}x` : '-'),
  },
  {
    id: 'orderItems',
    label: 'Itens do pedido',
    cell: (order) => summarizedCell(order.orderItemsSummary),
  },
  {
    id: 'client',
    label: 'Cliente',
    cell: (order) => order.client || 'N/A',
  },
  {
    id: 'orderDate',
    label: 'Data pedido',
    accessor: (order) => order.orderDate || order.createdAt,
    cell: (order) => formatDate(order.orderDate || order.createdAt),
  },
  {
    id: 'startDateOfDelivery',
    label: 'Data entrega',
    cell: (order) => formatDate(order.startDateOfDelivery),
  },
  {
    id: 'quantityItems',
    label: 'Qtd. de itens',
    cell: (order) => order.quantityItems || '0',
  },
  {
    id: 'quantityPieces',
    label: 'Qtd. de peças',
    cell: (order) => order.quantityPieces || '0',
  },
  {
    id: 'total',
    label: 'Total Cadastrado',
    accessor: (order) => getTotalValue(order),
    cell: (order) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(getTotalValue(order)),
  },
  {
    id: 'sellingPrice',
    label: 'Preço de Venda do Pedido',
    accessor: (order) => Number(order.sellingPrice) || 0,
    cell: (order) => formatCurrency(order.sellingPrice),
    exportValue: (order) => formatCurrency(order.sellingPrice),
  },
  {
    id: 'saleStatus',
    label: 'Venda',
    disableTooltip: true,
    cell: (order) => (
      <SaleStatusBadge $variant={getSaleStatusVariant(order.saleStatus)}>
        {order.saleStatus || '-'}
      </SaleStatusBadge>
    ),
  },
]
